import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Modal, Popconfirm, Row, Space, Spin, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import SectionCard from '../../../../components/shared/SectionCard';
import { useNotifications } from '../../../../context/NotificationContext';
import {
	createPatientOtherName,
	fetchPatientNames,
	replacePatientOtherName,
	supersedePatientOtherName,
} from '../../../../api/healthcare/PatientApi';
import {
	PatientNamesResponse,
	PatientOtherNameSubmission,
	PatientStructuredName,
} from '../../../../types/healthcare/PatientNameTypes';
import PatientHiAssociatedIndicator from './PatientHiAssociatedIndicator';
import PatientOtherNameFields from './PatientOtherNameFields';
import {
	HI_ASSOCIATED_NAME_HELP_ID,
	HI_ASSOCIATED_NAME_HELP_TEXT,
	ONE_NAME_ONLY_LABEL,
	PATIENT_NAMES_LOAD_FAILED_MESSAGE,
	PATIENT_OTHER_NAME_CREATED_MESSAGE,
	PATIENT_OTHER_NAME_REPLACED_MESSAGE,
	PATIENT_OTHER_NAME_SUPERSEDED_MESSAGE,
	mapPatientNameError,
	patientNameContextLabel,
	patientNameHistoryLabel,
	patientNameRoleParts,
	validatePatientOtherName,
} from './patientNameDisplay';
import type { UserUuid } from '../../../../types/identity';

const { Text, Title } = Typography;

/**
 * B1.4-E — the authenticated patient-name section of the Personal Info
 * composition.
 *
 * It presents three deterministic, separately labelled groups so the source
 * distinctions stay legible in both directions:
 *
 *  - the canonical PRIMARY name, with explicit given/middle/family roles
 *    (HI_005_005804 / HI_010_005804 / HI_015_005804);
 *  - ACTIVE OTHER names, which are not previous names (HI_010_005809 /
 *    HI_015_005809);
 *  - read-only NAME HISTORY — retained previous names and superseded other
 *    names, which are not active other names (HI_015_005824).
 *
 * Lifecycle boundaries this component preserves:
 *
 *  - an other name is changed only through the supersede-and-replace route, so
 *    the superseded entry stays retrievable in history;
 *  - there is no hard-delete and no restore action, and no lifecycle, ownership,
 *    provenance or HI-association field is ever client-selected;
 *  - history rows carry no controls at all;
 *  - the server projection is re-read after every successful mutation and after
 *    a canonical primary-name save, so a retired association or newly retained
 *    history entry can never be shown stale.
 *
 * Privacy: nothing here logs. Operator feedback uses the fixed bounded messages
 * only — no patient-name component, identifier, request body, response body,
 * provider error or database error is written to the console or interpolated
 * into a message.
 */

const EMPTY_SUBMISSION: PatientOtherNameSubmission = {
	family_name: '',
	given_name: null,
	middle_name: null,
	is_one_name_only: false,
	name_context: 'general',
};

const toSubmission = (name: PatientStructuredName): PatientOtherNameSubmission => ({
	family_name: name.family_name ?? '',
	given_name: name.given_name,
	middle_name: name.middle_name,
	is_one_name_only: name.is_one_name_only === true,
	name_context: name.name_context,
});

/** One labelled name row. `actions` is omitted entirely for read-only history. */
const NameRow: React.FC<{
	parts: Array<{ label: string; value: string }>;
	isOneNameOnly: boolean;
	isHiAssociated: boolean;
	lifecycleLabel?: string;
	contextLabel?: string;
	actions?: React.ReactNode;
}> = ({ parts, isOneNameOnly, isHiAssociated, lifecycleLabel, contextLabel, actions }) => (
	<Row
		gutter={[8, 4]}
		align="middle"
		style={{
			padding: '6px 0',
			borderTop: '1px solid var(--mg-border)',
		}}
	>
		<Col xs={24} md={actions ? 16 : 24}>
			<Space size={[8, 4]} wrap>
				{parts.length === 0 ? (
					<Text type="secondary" style={{ fontSize: 12 }}>
						No name components recorded.
					</Text>
				) : (
					parts.map((part) => (
						<span key={part.label} style={{ fontSize: 12 }}>
							<Text type="secondary" style={{ fontSize: 11 }}>
								{part.label}:{' '}
							</Text>
							<Text style={{ fontSize: 12 }}>{part.value}</Text>
						</span>
					))
				)}
				{isOneNameOnly && <Tag>{ONE_NAME_ONLY_LABEL}</Tag>}
				{lifecycleLabel && <Tag>{lifecycleLabel}</Tag>}
				{contextLabel && (
					<span style={{ fontSize: 12 }}>
						<Text type="secondary" style={{ fontSize: 11 }}>
							Context:{' '}
						</Text>
						<Text style={{ fontSize: 12 }}>{contextLabel}</Text>
					</span>
				)}
				{isHiAssociated && <PatientHiAssociatedIndicator />}
			</Space>
		</Col>
		{actions && (
			<Col xs={24} md={8} style={{ textAlign: 'right' }}>
				<Space size={6} wrap>
					{actions}
				</Space>
			</Col>
		)}
	</Row>
);

interface PatientNamesSectionProps {
	patientId: UserUuid;
	/**
	 * Incremented by the parent after a successful canonical primary-name save, so
	 * a retired primary association or a newly retained previous-primary entry is
	 * re-read from the server rather than left stale.
	 */
	primaryNameSaveSignal?: number;
}

const PatientNamesSection: React.FC<PatientNamesSectionProps> = ({
	patientId,
	primaryNameSaveSignal,
}) => {
	const { showSuccess, showError } = useNotifications();

	const [names, setNames] = useState<PatientNamesResponse | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [editingId, setEditingId] = useState<number | 'new' | null>(null);
	const [draft, setDraft] = useState<PatientOtherNameSubmission>(EMPTY_SUBMISSION);
	const [formErrors, setFormErrors] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const loadNames = useCallback(async () => {
		setIsLoading(true);
		setLoadError(null);
		try {
			const response = await fetchPatientNames(patientId);
			setNames(response);
		} catch (error) {
			// Only the bounded status category is used; no response content is read.
			setNames(null);
			setLoadError(PATIENT_NAMES_LOAD_FAILED_MESSAGE);
		} finally {
			setIsLoading(false);
		}
	}, [patientId]);

	// One effect for both triggers, so a primary-name save refreshes the
	// projection without the initial mount fetching twice.
	useEffect(() => {
		void loadNames();
	}, [loadNames, primaryNameSaveSignal]);

	const closeForm = () => {
		setEditingId(null);
		setDraft(EMPTY_SUBMISSION);
		setFormErrors([]);
	};

	const handleSubmitForm = async () => {
		const errors = validatePatientOtherName(draft);
		if (errors.length > 0) {
			setFormErrors(errors);
			return;
		}

		setIsSaving(true);
		try {
			if (editingId === 'new') {
				await createPatientOtherName(patientId, draft);
				showSuccess(PATIENT_OTHER_NAME_CREATED_MESSAGE);
			} else if (typeof editingId === 'number') {
				// Supersede-and-replace: the existing row is never overwritten in
				// place, so the old entry remains retrievable in the history group.
				await replacePatientOtherName(patientId, editingId, draft);
				showSuccess(PATIENT_OTHER_NAME_REPLACED_MESSAGE);
			}
			closeForm();
			await loadNames();
		} catch (error) {
			setFormErrors([mapPatientNameError(error)]);
		} finally {
			setIsSaving(false);
		}
	};

	const handleSupersede = async (patientNameId: number) => {
		try {
			await supersedePatientOtherName(patientId, patientNameId);
			showSuccess(PATIENT_OTHER_NAME_SUPERSEDED_MESSAGE);
			await loadNames();
		} catch (error) {
			showError(mapPatientNameError(error));
		}
	};

	const activeOtherNames = names?.active_other_names ?? [];
	const nameHistory = names?.name_history ?? [];

	// The shared help text is rendered once when at least one indication is on
	// screen, and every indicator is described by it.
	const hasAnyIndication =
		names?.primary_name?.is_hi_associated === true ||
		activeOtherNames.some((n) => n.is_hi_associated) ||
		nameHistory.some((n) => n.is_hi_associated);

	return (
		<SectionCard title="Patient Names">
			{isLoading && (
				<div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<Spin size="small" />
					<Text type="secondary" style={{ fontSize: 12 }}>
						Loading patient names…
					</Text>
				</div>
			)}

			{!isLoading && loadError && (
				<Alert
					type="error"
					showIcon
					message={loadError}
					action={
						<Button size="small" onClick={() => void loadNames()}>
							Retry
						</Button>
					}
				/>
			)}

			{!isLoading && !loadError && names && (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
					{hasAnyIndication && (
						<Text
							id={HI_ASSOCIATED_NAME_HELP_ID}
							type="secondary"
							style={{ fontSize: 11 }}
						>
							{HI_ASSOCIATED_NAME_HELP_TEXT}
						</Text>
					)}

					<div>
						<Title level={5} style={{ fontSize: 13, marginBottom: 0 }}>
							Primary name
						</Title>
						<NameRow
							parts={patientNameRoleParts(names.primary_name)}
							isOneNameOnly={names.primary_name?.is_one_name_only === true}
							isHiAssociated={names.primary_name?.is_hi_associated === true}
						/>
					</div>

					<div>
						<Row justify="space-between" align="middle" gutter={[8, 4]}>
							<Col>
								<Title level={5} style={{ fontSize: 13, marginBottom: 0 }}>
									Active other names
								</Title>
							</Col>
							<Col>
								<Button
									size="small"
									icon={<PlusOutlined />}
									onClick={() => {
										setDraft(EMPTY_SUBMISSION);
										setFormErrors([]);
										setEditingId('new');
									}}
								>
									Add other name
								</Button>
							</Col>
						</Row>
						{activeOtherNames.length === 0 ? (
							<Text type="secondary" style={{ fontSize: 12 }}>
								No other names recorded.
							</Text>
						) : (
							activeOtherNames.map((name) => (
								<NameRow
									key={name.id}
									parts={patientNameRoleParts(name)}
									isOneNameOnly={name.is_one_name_only === true}
									isHiAssociated={name.is_hi_associated === true}
									contextLabel={patientNameContextLabel(name.name_context)}
									actions={
										<>
											<Button
												size="small"
												onClick={() => {
													setDraft(toSubmission(name));
													setFormErrors([]);
													setEditingId(name.id);
												}}
											>
												Edit
											</Button>
											{/*
											 * Supersession is explicit and confirmed. There is
											 * deliberately no delete and no restore control: a
											 * recorded name must stay retrievable once retired.
											 */}
											<Popconfirm
												title="Supersede this other name?"
												description="It will be retained in the name history and can no longer be edited."
												okText="Confirm"
												cancelText="Cancel"
												onConfirm={() => void handleSupersede(name.id)}
											>
												<Button size="small">Supersede</Button>
											</Popconfirm>
										</>
									}
								/>
							))
						)}
					</div>

					<div>
						<Title level={5} style={{ fontSize: 13, marginBottom: 0 }}>
							Name history
						</Title>
						<Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
							Read-only. Retained previous names and superseded other names.
						</Text>
						{nameHistory.length === 0 ? (
							<Text type="secondary" style={{ fontSize: 12 }}>
								No previous or superseded names recorded.
							</Text>
						) : (
							nameHistory.map((name) => (
								<NameRow
									key={name.id}
									parts={patientNameRoleParts(name)}
									isOneNameOnly={name.is_one_name_only === true}
									isHiAssociated={name.is_hi_associated === true}
									lifecycleLabel={patientNameHistoryLabel(name)}
									contextLabel={patientNameContextLabel(name.name_context)}
								/>
							))
						)}
					</div>
				</div>
			)}

			<Modal
				open={editingId !== null}
				title={editingId === 'new' ? 'Add other name' : 'Update other name'}
				okText="Save"
				confirmLoading={isSaving}
				onOk={() => void handleSubmitForm()}
				onCancel={closeForm}
				destroyOnClose
			>
				{editingId !== null && typeof editingId === 'number' && (
					<Alert
						type="info"
						showIcon
						style={{ marginBottom: 12 }}
						message="Saving records a new active other name and retains the current entry in the name history."
					/>
				)}
				{formErrors.length > 0 && (
					<Alert
						type="error"
						showIcon
						style={{ marginBottom: 12 }}
						message={formErrors.join(' ')}
					/>
				)}
				<PatientOtherNameFields
					idPrefix="patient-other-name"
					value={draft}
					onChange={setDraft}
					disabled={isSaving}
				/>
			</Modal>
		</SectionCard>
	);
};

export default PatientNamesSection;
